import { useTranslation } from 'react-i18next';
import message from 'antd/es/message';
import cx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { toastError, useCurrentUserId } from '@/utils';
import { ImageUpload, HelperTooltip, Loading, NavLink } from 'components';
import {
  useCommonImageUpload,
  usePaidFeatureAvailability,
  PAID_FEATURE,
  useUserBanner,
  updateUserBannerQuery,
  Web3IdGiftOrder,
} from 'hooks';
import type { RcFile } from 'antd/es/upload';
import {
  IconUploadBanner,
  IconUploadBannerDark,
  IconUploadBannerDisabled,
  IconDeleteBanner,
} from '@/assets/icon/avatar';
import { useUpdateBannerUrl } from './hook';
import style from './index.module.scss';

interface UserBannerUploadProps {
  address: string;
  userInfo?: UserInfo;
  showBannerEdit?: boolean;
  className?: string;
  giftOrder?: Web3IdGiftOrder;
}

export const SIZE_LIMIT = 5 * 1024 * 1024;
export const PIXEL_LIMIT = 100;

export const UserBannerUpload = ({
  address,
  className,
  giftOrder,
  showBannerEdit,
}: UserBannerUploadProps) => {
  const { t } = useTranslation();

  const { mutateAsync: updateBannerUrl, isLoading: isUpdating } = useUpdateBannerUrl();
  const history = useHistory();
  const { mutateAsync: uploadImage, isLoading: isUploading } = useCommonImageUpload('banner');
  const { data: enableCustomBannerFeature } = usePaidFeatureAvailability(
    PAID_FEATURE.PROFILE_BANNER,
  );
  const userId = useCurrentUserId();

  const validGiftOrder = useMemo(() => giftOrder && !giftOrder?.is_minted, [giftOrder]);
  const isGiftBuyer = useMemo(
    () => Boolean(giftOrder?.buyer.id === userId && validGiftOrder),
    [giftOrder?.buyer.id, userId, validGiftOrder],
  );
  const enableEditCustomBanner = useMemo(
    () => enableCustomBannerFeature || isGiftBuyer,
    [enableCustomBannerFeature, isGiftBuyer],
  );
  const { data: bannerData } = useUserBanner(address);

  const hasBanner = useMemo(() => Boolean(bannerData?.banner_url), [bannerData?.banner_url]);

  const gotoPaidFeature = useCallback(() => history.push('/paid'), [history]);

  const handleBeforeUpload = useCallback(
    (file: RcFile) => {
      return new Promise<void>(async (resolve, reject) => {
        if (file.size > SIZE_LIMIT) {
          message.error(t('UserBanner.sizeLimit'));
          return reject();
        }

        try {
          const imageRes = await uploadImage(file).catch(e => {
            message.warn(e);
          });
          if (imageRes?.image?.id) {
            const banner_url = imageRes?.image?.raw?.full_url;
            const banner_thumbnail_url = imageRes?.image?.resize?.full_url;
            updateBannerUrl({
              url: banner_url,
              thumbnail_url: banner_thumbnail_url,
              id: address,
            }).then(async ({ is_success }) => {
              if (!is_success) {
                message.warn(t('UserBanner.failed'));
                return;
              }

              updateUserBannerQuery(address, {
                banner_url,
                banner_thumbnail_url,
              });
              message.success(t('UserBanner.changed'));
            });
          }
        } catch (error) {
          toastError(error || t('UserBanner.upload-failed'));
        }
      });
    },
    [address, updateBannerUrl, uploadImage, t],
  );

  const deleteBannerHandler = useCallback(() => {
    updateBannerUrl({
      url: '',
      thumbnail_url: '',
      id: address,
    }).then(async ({ is_success }) => {
      if (!is_success) {
        message.warn(t('UserBanner.failed'));
        return;
      }

      updateUserBannerQuery(address, {
        banner_url: '',
        banner_thumbnail_url: '',
      });

      message.success(t('UserBanner.cleared'));
    });
  }, [address, updateBannerUrl, t]);

  return (
    <div className={cx(style.userBannerUpload, hasBanner && style.hasBanner, className)}>
      {showBannerEdit ? (
        <>
          {enableEditCustomBanner ? (
            isUploading || isUpdating ? (
              <Loading />
            ) : (
              <>
                <HelperTooltip
                  placement="left"
                  title={
                    <>
                      <div>{t('UserBanner.1ftxzap')}</div>
                      <div>{t('UserBanner.1f5a8nt')}</div>
                    </>
                  }
                  disable={!isGiftBuyer}>
                  <ImageUpload
                    placeholder={hasBanner ? <IconUploadBannerDark /> : <IconUploadBanner />}
                    beforeUpload={handleBeforeUpload}
                  />
                </HelperTooltip>

                {hasBanner && (
                  <div className={style.deleteButton} onClick={deleteBannerHandler}>
                    <IconDeleteBanner />
                  </div>
                )}
              </>
            )
          ) : (
            <HelperTooltip
              placement="left"
              overlayClassName={style.subscribeTooltip}
              title={
                <>
                  {t('UserBanner.1co23nd')}{' '}
                  <span className={style.subscribeLink} onClick={gotoPaidFeature}>
                    {t('UserBanner.1c37jyp')}
                  </span>
                </>
              }>
              <IconUploadBannerDisabled />
            </HelperTooltip>
          )}
        </>
      ) : null}

      {validGiftOrder && hasBanner ? (
        <div className={style.giftLabel}>
          {t('UserBanner.3s9hpc')}
          <NavLink to={`/profile/${giftOrder?.buyer?.id}`} className={style.name} target="_blank">
            {giftOrder?.buyer.web3_id}
          </NavLink>
        </div>
      ) : null}
    </div>
  );
};
